import styled from '@emotion/styled';
import { LogOut, Menu } from 'lucide-react';
import { useAuth } from '@/features/auth';
import { colors, elevation, spacing, typography } from '@/lib/design-tokens';
import { useUIStore } from '@/stores/ui-store';

const HEADER_HEIGHT = '56px';

const StyledHeader = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: ${HEADER_HEIGHT};
  padding: 0 ${spacing[400]};
  background-color: ${colors.neutral[0]};
  border-bottom: 1px solid ${colors.neutral[200]};
  box-shadow: ${elevation.raised};
  flex-shrink: 0;
  position: sticky;
  top: 0;
  z-index: 50;
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[300]};
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[300]};
`;

const IconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 4px;
  color: ${colors.neutral[700]};
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: rgba(9, 30, 66, 0.06);
  }

  &:focus-visible {
    outline: 2px solid ${colors.brand.blue[300]};
    outline-offset: 2px;
  }
`;

const UserSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[250]};
`;

const UserName = styled.span`
  font-family: ${typography.fontFamily.default};
  font-size: ${typography.fontSize.body};
  font-weight: ${typography.fontWeight.medium};
  color: ${colors.neutral[700]};
`;

const LogoutButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${spacing[100]};
  padding: ${spacing[100]} ${spacing[250]};
  background: transparent;
  border: none;
  border-radius: 4px;
  font-family: ${typography.fontFamily.default};
  font-size: ${typography.fontSize.body};
  font-weight: ${typography.fontWeight.regular};
  color: ${colors.neutral[600]};
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease;

  &:hover {
    background-color: rgba(9, 30, 66, 0.06);
    color: ${colors.neutral[900]};
  }

  &:focus-visible {
    outline: 2px solid ${colors.brand.blue[300]};
    outline-offset: 2px;
  }
`;

function Header() {
  const { toggleSidebar } = useUIStore();
  const { user, logout } = useAuth();

  return (
    <StyledHeader>
      <LeftSection>
        <IconButton
          type="button"
          onClick={toggleSidebar}
          aria-label="ナビゲーションメニューを開閉する"
        >
          <Menu size={20} aria-hidden="true" />
        </IconButton>
      </LeftSection>
      <RightSection>
        {user !== null && (
          <UserSection>
            <UserName>{user.user_name}</UserName>
            <LogoutButton type="button" onClick={logout} aria-label="ログアウト">
              <LogOut size={16} aria-hidden="true" />
              ログアウト
            </LogoutButton>
          </UserSection>
        )}
      </RightSection>
    </StyledHeader>
  );
}

export { Header };
